/**
 * AYUNEL 商品页「みんなの着こなし動画」—— blocks/ayunel-ugc-videos.liquid 的行为层(新房 Horizon 4.1.4)。2026-09-16。
 *
 * 只管三件事:哪条视频该预览、电脑端两颗翻页钮、点卡打开有声弹窗。文案、链接、卡片、弹窗 markup 全在 Liquid,这里不拼一个字。
 * 以 <script type="module"> 引入(与三标签 / 套装块同姿势)。
 *
 * 播放:
 * - 每个 <video> 都是 preload="none" + poster,页面加载时一个字节的视频都不下。
 * - IntersectionObserver 看每张卡:≥60% 进视野就 play()(静音、循环、playsinline 全在 Liquid 手写的 <video> 上;
 *   isIntersecting 之外再比 intersectionRatio —— 规范里 isIntersecting 只表示「有没有相交」,Firefox 露 1% 就算),
 *   滑出视野 / 标签页切走就 pause()。横滑容器嵌在 .page-wrapper 里,IO 默认 root 会一起算祖先裁切,不用另配 root。
 * - 不自动播的四种情况(只留封面,点卡照样跳 IG):prefers-reduced-motion: reduce / navigator.connection.saveData /
 *   慢网络(navigator.connection.effectiveType 是 2g / slow-2g / 3g,Chrome · Android 才有这个值,Safari 没有就当不慢)/
 *   后台把「自动播放」关掉(宿主 data-autoplay="false")。Liquid 只拿得到 HLS 与 1080p mp4(见块头注):Safari / Chrome 走 HLS 按带宽自适应,
 *   Firefox 落 1080p mp4 一条约 7MB —— 慢网络这条保险不是锦上添花。
 * - play() 的 Promise 被拒(低电量模式等)就当没播过,不重试、不报错。
 *
 * 翻页钮:
 * - 只在 CSS 判定为 hover + fine pointer 的设备上显示(手机靠露半张卡提示可滑);滑不动(内容没溢出)时整组 hidden。
 * - 一次滑一张卡(卡宽 + 间距),smooth(reduced-motion 下 auto);两端各自 aria-disabled —— **不用原生 disabled**:
 *   键盘连按到底的那一下,按钮一 disabled 焦点就掉回 body(复审 B 2026-09-16 实测,WCAG 2.4.3)。scroll / ResizeObserver 都会重算。
 * - focusin:Tab 到只露一条边的卡时 Chrome 判「部分可见」不自动滚,焦点环几乎看不见 ⇒ 把卡 scrollIntoView(inline nearest)。
 *
 * 切走标签页:只 video.pause(),**不动 aynInView 标记** —— 标记是切回来时判「谁该继续播」的唯一依据
 * (复审 B 实测:隐藏路径若走 #pause() 把标记删光,切回来一条都不播)。
 *
 * 弹窗(智鹏 2026-09-16 深夜:点视频要当页弹窗、有声;@账号才跳 IG;弹窗里也给跳 IG 的入口):
 * - 点卡(<button data-ref="open">)→ 把该卡 <video> 的 <source> 与 poster 克隆进弹窗播放器(不另下一份),
 *   showModal + html[scroll-lock](与サイズ診断同姿势),横滑条全部暂停,播放器 muted=false 再 play() —— 在点击手势里发起,浏览器允许有声。
 * - 关(✕ / 背景 / Esc)统一走 dialog 的 close 事件收尾:停播、清空 source(poster 留着,淡出期显示封面不是黑框)、解 scroll-lock、续播视野里的预览、焦点回到那张卡。
 * - 切走标签页(含点「Instagramで見る」开新标签)必须把弹窗播放器也停掉 —— 否则有声 reel 在后台标签无限循环(复审 D 实测);切回来不自动续播,交给控制条。
 * - 宿主断连时 close 事件是异步派发、监听已被 abort ⇒ 收尾要同步手动调一次,不然 html[scroll-lock] 永久残留(复审 C/D 实测 host.remove() 后整页锁死)。
 * - focusin → scrollIntoView 只在键盘聚焦(:focus-visible)时做:鼠标 / 手指点半露的卡也会 focusin,block:'nearest' 会把页面竖着滚 84px(复审 C 实测)。
 * - 弹窗顶栏写 @账号,底栏「Instagramで見る」链接 = 那条 reel 的 permalink。
 *
 * 清理:所有监听走一个 AbortController,disconnectedCallback 一刀切;IO / RO 同时 disconnect。
 * 宿主由 Liquid 双挂 morph skip,换色码时整棵子树不会被服务端 HTML 覆盖 —— 所以本组件不需要处理「被替换后重来」。
 * 守卫:tests/test_theme_pdp_ugc_videos.py(字符串级契约)。
 */
const TAG = 'ayunel-ugc-videos';
const PLAY_THRESHOLD = 0.6;
const SLOW_NETWORKS = new Set(['slow-2g', '2g', '3g']);

class AyunelUgcVideos extends HTMLElement {
  #controller;
  #intersection;
  #resize;
  #opener = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.list = this.querySelector('[data-ref="list"]');
    this.nav = this.querySelector('[data-ref="nav"]');
    this.prev = this.querySelector('[data-ref="prev"]');
    this.next = this.querySelector('[data-ref="next"]');
    this.videos = [...this.querySelectorAll('.ayunel-ugc-videos__video')];
    this.dialog = this.querySelector('[data-ref="dialog"]');
    this.player = this.querySelector('[data-ref="player"]');
    this.dialogTitle = this.querySelector('[data-ref="dialog-title"]');
    this.dialogLink = this.querySelector('[data-ref="dialog-link"]');

    if (this.#canAutoplay()) {
      this.#intersection = new IntersectionObserver((entries) => this.#onIntersect(entries), { threshold: PLAY_THRESHOLD });
      this.videos.forEach((video) => this.#intersection.observe(video));
    }
    // 手动有声播放也必须随页面隐藏暂停,不受自动预览开关 / 省流 / 减少动态效果设置影响。
    document.addEventListener('visibilitychange', () => this.#onVisibility(), { signal });

    if (this.list && this.nav && this.prev && this.next) {
      this.prev.addEventListener('click', () => this.#scrollByCard(-1), { signal });
      this.next.addEventListener('click', () => this.#scrollByCard(1), { signal });
      this.list.addEventListener('scroll', () => this.#syncNav(), { signal, passive: true });
      this.list.addEventListener('focusin', (event) => this.#revealFocused(event.target), { signal });
      if (typeof ResizeObserver === 'function') {
        this.#resize = new ResizeObserver(() => this.#syncNav());
        this.#resize.observe(this.list);
      }
      this.#syncNav();
    }

    if (this.dialog && this.player) {
      this.querySelectorAll('[data-ref="open"]').forEach((button) => {
        button.addEventListener('click', () => this.#openDialog(button), { signal });
      });
      this.querySelector('[data-ref="close"]')?.addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, { signal });
      this.dialog.addEventListener('close', () => this.#onDialogClose(), { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#intersection?.disconnect();
    this.#resize?.disconnect();
    this.videos.forEach((video) => this.#pause(video));
    if (this.dialog?.open) {
      this.dialog.close();
      this.#onDialogClose();
    }
    document.documentElement.removeAttribute('scroll-lock');
  }

  #openDialog(button) {
    const source = button.querySelector('.ayunel-ugc-videos__video');
    if (!source) return;
    this.#opener = button;
    this.dialogTitle.textContent = button.dataset.handle || '';
    this.dialogLink.href = button.dataset.permalink || '#';
    this.player.replaceChildren(...[...source.querySelectorAll('source')].map((node) => node.cloneNode(true)));
    this.player.poster = source.poster;
    this.player.load();
    this.videos.forEach((video) => { if (!video.paused) video.pause(); });
    this.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.player.muted = false;
    const attempt = this.player.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  #onDialogClose() {
    this.player.pause();
    this.player.replaceChildren();
    this.player.load();
    document.documentElement.removeAttribute('scroll-lock');
    this.videos.forEach((video) => { if (video.dataset.aynInView === '1') this.#play(video); });
    if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
    this.#opener = null;
  }

  #canAutoplay() {
    if (this.dataset.autoplay === 'false') return false;
    if (this.#reducedMotion()) return false;
    if (navigator.connection?.saveData) return false;
    if (SLOW_NETWORKS.has(navigator.connection?.effectiveType)) return false;
    return true;
  }

  #onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD && !document.hidden) {
        entry.target.dataset.aynInView = '1';
        if (!this.dialog?.open) this.#play(entry.target);
      } else {
        this.#pause(entry.target);
      }
    }
  }

  #onVisibility() {
    if (document.hidden) {
      this.videos.forEach((video) => { if (!video.paused) video.pause(); });
      if (this.player && !this.player.paused) this.player.pause();
      return;
    }
    if (this.dialog?.open) return;
    this.videos.forEach((video) => {
      if (video.dataset.aynInView === '1') this.#play(video);
    });
  }

  #play(video) {
    video.muted = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  #pause(video) {
    if (video.dataset.aynInView === '1') delete video.dataset.aynInView;
    if (!video.paused) video.pause();
  }

  #cardStep() {
    const item = this.list.querySelector('.ayunel-ugc-videos__item');
    if (!item) return this.list.clientWidth;
    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  #reducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #scrollByCard(direction) {
    const button = direction < 0 ? this.prev : this.next;
    if (button.getAttribute('aria-disabled') === 'true') return;
    this.list.scrollBy({ left: direction * this.#cardStep(), behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
  }

  #revealFocused(target) {
    const card = target instanceof Element ? target.closest('.ayunel-ugc-videos__card') : null;
    if (!card || !card.matches(':focus-visible')) return;
    card.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: this.#reducedMotion() ? 'auto' : 'smooth' });
  }

  #syncNav() {
    const overflow = this.list.scrollWidth - this.list.clientWidth;
    if (overflow <= 1) {
      this.nav.hidden = true;
      return;
    }
    this.nav.hidden = false;
    const position = this.list.scrollLeft;
    this.prev.setAttribute('aria-disabled', String(position <= 1));
    this.next.setAttribute('aria-disabled', String(position >= overflow - 1));
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, AyunelUgcVideos);
